// Office Hours — per-tenant business hours.
// Layout matches 3CX (spec section "Office Hours per-Department page"):
//   • Left rail: tenant/department selector (alphabetical)
//   • Right pane:
//     - Tabs: Office Hours | Office Holidays
//     - Office Hours: timezone picker + 7-col day grid (multi-range) + Break grid
//     - Office Holidays: redirect to /holidays (canonical store)
// Stored via GET/PUT /api/office-hours/:domainId (v_domain_settings
// stratus/office_hours/schedule — the SAME row stratus_routing_vars.lua and
// auto_queue_logout read; #907. The old save path, PUT /api/tenants/:id,
// silently dropped office_hours). Canonical wire shape:
//   { timezone, officeHours: { monday: [{start,end},...], ... sunday },
//     breakHours: { ... } }
// Internally this page keeps its short-day-key shape ('mon'...) and converts
// at the API boundary via lib/officeHoursShape.
//
// NOTE (#991): The HolidaysTab was removed because it wrote to v_domain_settings JSONB
// which FreeSWITCH routing Lua never reads. stratus_routing_vars.lua reads
// stratus_ext.holidays exclusively (populated by /holidays via /api/call-routing/holidays).
// The Office Holidays tab now redirects users to the canonical /holidays page.

import { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { getActiveTenant, ALL, matchesScope } from '../lib/tenantScope';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { X, Save } from 'lucide-react';
import { getToken } from '../lib/api';
import { canonicalToUi, uiToCanonical } from '../lib/officeHoursShape';

const auth = () => ({ headers: { Authorization: `Bearer ${getToken()}` } });
const fJson = async (url: string, init: RequestInit = {}) => {
  const r = await fetch(url, { ...init, headers: { ...auth().headers, ...(init.headers || {}) } });
  if (!r.ok) throw new Error(await r.text());
  return r.json();
};

const DAYS: { key: 'mon'|'tue'|'wed'|'thu'|'fri'|'sat'|'sun'; label: string }[] = [
  { key: 'mon', label: 'Monday' },
  { key: 'tue', label: 'Tuesday' },
  { key: 'wed', label: 'Wednesday' },
  { key: 'thu', label: 'Thursday' },
  { key: 'fri', label: 'Friday' },
  { key: 'sat', label: 'Saturday' },
  { key: 'sun', label: 'Sunday' },
];

// Curated 12-entry timezone list. Full IANA tz database is 600+ — this covers
// the US/Americas common cases. Extend as needed.
const TIMEZONES = [
  { v: 'America/New_York',     label: '-5:00 United States — Eastern Time' },
  { v: 'America/Chicago',      label: '-6:00 United States — Central Time' },
  { v: 'America/Denver',       label: '-7:00 United States — Mountain Time' },
  { v: 'America/Phoenix',      label: '-7:00 United States — Arizona' },
  { v: 'America/Los_Angeles',  label: '-8:00 United States — Pacific Time' },
  { v: 'America/Anchorage',    label: '-9:00 United States — Alaska' },
  { v: 'Pacific/Honolulu',     label: '-10:00 United States — Hawaii' },
  { v: 'America/Toronto',      label: '-5:00 Canada — Eastern' },
  { v: 'America/Vancouver',    label: '-8:00 Canada — Pacific' },
  { v: 'Europe/London',        label: '+0:00 United Kingdom' },
  { v: 'Europe/Paris',         label: '+1:00 Central Europe' },
  { v: 'UTC',                  label: '+0:00 UTC' },
];

type Range = { start: string; end: string };
type DayRanges = Record<string, Range[]>;

export default function OfficeHours() {
  const [params, setParams] = useSearchParams();
  const qc = useQueryClient();
  const [tab, setTab] = useState<'hours' | 'holidays'>('hours');

  // ── Left rail: list of tenants/departments ──────────────────────────────
  const tenantsQ = useQuery<any[]>({
    queryKey: ['tenants'],
    queryFn: () => fJson('/api/tenants'),
  });
  // Skip nameless / empty entries — those render as a blank blue bar in the
  // rail when auto-selected. A real tenant must have at least a domain_uuid
  // AND a description or name to display.
  const tenants = (tenantsQ.data || [])
    .filter((t: any) => t?.domain_uuid && (t.domain_description?.trim() || t.domain_name?.trim()))
    .filter((t: any) => matchesScope(t.domain_uuid))   // global dept filter: rail shows only the selected business
    .slice()
    .sort((a: any, b: any) =>
      (a.domain_description || a.domain_name || '').localeCompare(b.domain_description || b.domain_name || ''));

  const _scope = getActiveTenant();
  const selectedDomain = (_scope !== ALL ? _scope : params.get('domain_uuid')) || tenants[0]?.domain_uuid || '';
  function selectDept(d: string) {
    const next = new URLSearchParams(params);
    next.set('domain_uuid', d);
    setParams(next, { replace: true });
  }
  // Auto-select first tenant once loaded
  useEffect(() => {
    if (_scope === ALL && !params.get('domain_uuid') && tenants.length > 0) {
      selectDept(tenants[0].domain_uuid);
    }
  }, [tenants.length]);

  // ── Right pane data: tenant (identity) + the office-hours document ────────
  const { data: tenant } = useQuery<any>({
    queryKey: ['tenant-detail', selectedDomain],
    queryFn: () => fJson(`/api/tenants/${selectedDomain}`),
    enabled: !!selectedDomain,
  });
  // The REAL schedule store (#907) — the same row FreeSWITCH's routing Lua and
  // the auto-queue-logout worker read. The old tenant PUT dropped office_hours.
  const { data: ohDoc } = useQuery<any>({
    queryKey: ['office-hours', selectedDomain],
    queryFn: () => fJson(`/api/office-hours/${selectedDomain}`),
    enabled: !!selectedDomain,
  });

  return (
    <div className="space-y-3">
      <h1 className="text-2xl font-semibold">Office Hours</h1>

      <div className="flex gap-4 min-h-[600px]">
        {/* Left rail — alphabetical department list */}
        <div className="w-56 flex-shrink-0 space-y-1 max-h-[80vh] overflow-y-auto">
          {tenants.map((t: any) => (
            <button
              key={t.domain_uuid}
              onClick={() => selectDept(t.domain_uuid)}
              className={`w-full text-left px-3 py-2 rounded-md text-sm ${
                selectedDomain === t.domain_uuid ? 'bg-primary text-white' : 'hover:bg-elevated'
              }`}
            >
              {t.domain_description || t.domain_name}
            </button>
          ))}
        </div>

        {/* Right pane */}
        <div className="flex-1 min-w-0 card p-4 space-y-4">
          {/* Tabs */}
          <div className="flex gap-1 border-b border-border -mt-1">
            <button
              onClick={() => setTab('hours')}
              className={`px-4 py-2 text-sm ${tab === 'hours' ? 'border-b-2 border-primary text-primary' : 'text-textSecondary'}`}
            >Office Hours</button>
            <button
              onClick={() => setTab('holidays')}
              className={`px-4 py-2 text-sm ${tab === 'holidays' ? 'border-b-2 border-primary text-primary' : 'text-textSecondary'}`}
            >Office Holidays</button>
          </div>

          {tab === 'hours' && tenant && ohDoc && (
            <HoursTab tenant={tenant} doc={ohDoc} qc={qc} />
          )}
          {tab === 'holidays' && (
            <HolidaysRedirect />
          )}
          {!tenant && <div className="text-textSecondary text-sm">Select a department to configure.</div>}
        </div>
      </div>
    </div>
  );
}

// ── Office Hours tab — timezone + Office grid + Break grid ─────────────────
function HoursTab({ tenant, doc, qc }: any) {
  const oh = doc || {};
  // Canonical wire shape → UI shape. A doc with zero configured ranges gets
  // the 9-5 workday prefill ONLY when the tenant never saved hours
  // (configured:false from the API) — a deliberately saved all-closed
  // schedule stays empty.
  const initHours = () => {
    const ui = canonicalToUi(oh.officeHours);
    return ui ? normalizeRanges(ui, false) : normalizeRanges(undefined, oh.configured !== true);
  };
  const initBreaks = () => normalizeRanges(canonicalToUi(oh.breakHours), false);
  const [tz, setTz] = useState<string>(oh.timezone || 'America/New_York');
  const [hours, setHours] = useState<DayRanges>(initHours);
  const [breaks, setBreaks] = useState<DayRanges>(initBreaks);

  // Re-init when tenant changes or the doc (re)loads
  useEffect(() => {
    setTz(oh.timezone || 'America/New_York');
    setHours(initHours());
    setBreaks(initBreaks());
  }, [tenant?.domain_uuid, doc]);

  const save = useMutation({
    mutationFn: () => fJson(`/api/office-hours/${tenant.domain_uuid}`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...oh, timezone: tz, officeHours: uiToCanonical(hours), breakHours: uiToCanonical(breaks) }),
    }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['office-hours', tenant.domain_uuid] }),
  });

  return (
    <div className="space-y-5">
      <div>
        <h2 className="font-semibold">Set your time zone</h2>
        <select className="input mt-2" value={tz} onChange={e => setTz(e.target.value)}>
          {TIMEZONES.map(t => <option key={t.v} value={t.v}>{t.label}</option>)}
        </select>
      </div>

      <div>
        <h2 className="font-semibold">Set your office and break times</h2>
      </div>

      {/* Office Hours grid */}
      <RangeGrid
        title="Office Hours"
        ranges={hours}
        onChange={setHours}
      />

      {/* Break Hours grid */}
      <RangeGrid
        title="Break Hours"
        ranges={breaks}
        onChange={setBreaks}
      />

      <div className="flex justify-end">
        <button className="btn-primary inline-flex items-center gap-1" disabled={save.isPending} onClick={() => save.mutate()}>
          <Save size={14} /> {save.isPending ? 'Saving…' : 'Save'}
        </button>
      </div>
    </div>
  );
}

function normalizeRanges(input: any, defaultWorkdays: boolean): DayRanges {
  const out: DayRanges = {};
  for (const d of DAYS) {
    if (Array.isArray(input?.[d.key])) {
      out[d.key] = input[d.key];
    } else if (input?.[d.key]?.start) {
      // Old single-range shape — migrate
      out[d.key] = [{ start: input[d.key].start, end: input[d.key].end }];
    } else if (defaultWorkdays && ['mon','tue','wed','thu','fri'].includes(d.key)) {
      out[d.key] = [{ start: '09:00', end: '17:00' }];
    } else {
      out[d.key] = [];
    }
  }
  return out;
}

// 7-column day grid with multiple time ranges per day + per-day Add link.
function RangeGrid({ title, ranges, onChange }: { title: string; ranges: DayRanges; onChange: (r: DayRanges) => void }) {
  function addRange(day: string) {
    onChange({ ...ranges, [day]: [...(ranges[day] || []), { start: '09:00', end: '17:00' }] });
  }
  function clearAll() {
    onChange(Object.fromEntries(DAYS.map(d => [d.key, []])) as DayRanges);
  }
  function updateRange(day: string, idx: number, patch: Partial<Range>) {
    const next = (ranges[day] || []).slice();
    next[idx] = { ...next[idx], ...patch };
    onChange({ ...ranges, [day]: next });
  }
  function deleteRange(day: string, idx: number) {
    const next = (ranges[day] || []).slice();
    next.splice(idx, 1);
    onChange({ ...ranges, [day]: next });
  }

  return (
    <div className="border-t border-border pt-3">
      <div className="flex items-center justify-between mb-2">
        <div className="font-medium">{title}</div>
        <div className="space-x-3 text-sm">
          <button className="text-primary hover:underline" onClick={() => DAYS.forEach(d => addRange(d.key))}>Add</button>
          <button className="text-primary hover:underline" onClick={clearAll}>Clear</button>
        </div>
      </div>

      <div className="grid grid-cols-7 gap-2 text-xs">
        {DAYS.map(d => (
          <div key={d.key} className="space-y-1.5">
            <div className="font-medium">{d.label}</div>
            {(ranges[d.key] || []).map((r, idx) => (
              <div key={idx} className="space-y-0.5">
                <div className="flex items-center gap-1">
                  <input
                    type="time"
                    className="input p-1 text-xs flex-1 min-w-0"
                    value={r.start}
                    onChange={e => updateRange(d.key, idx, { start: e.target.value })}
                  />
                  <button onClick={() => deleteRange(d.key, idx)} className="text-textSecondary hover:text-danger" title="Delete range">
                    <X size={10} />
                  </button>
                </div>
                <input
                  type="time"
                  className="input p-1 text-xs w-full"
                  value={r.end}
                  onChange={e => updateRange(d.key, idx, { end: e.target.value })}
                />
              </div>
            ))}
            <button onClick={() => addRange(d.key)} className="text-primary hover:underline text-xs">Add</button>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Office Holidays redirect — canonical store is /holidays ───────────────
// (#991) The old HolidaysTab wrote to v_domain_settings JSONB (not read by
// FreeSWITCH). stratus_routing_vars.lua reads stratus_ext.holidays exclusively,
// which is populated via /api/call-routing/holidays on the /holidays page.
function HolidaysRedirect() {
  return (
    <div className="py-8 flex flex-col items-center gap-4 text-center">
      <p className="text-textSecondary text-sm max-w-md">
        Holidays are managed on the <strong>Holidays</strong> page, where they are saved to the
        routing engine. Changes made there take effect immediately in FreeSWITCH call routing.
      </p>
      <Link to="/holidays" className="btn-primary">Go to Holidays</Link>
    </div>
  );
}

